import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { apiClient } from "@/api/client";
import { queryClient } from "@/api/queryClient";
import { useAuthStore, AuthState } from "@/stores/useAuthStore";
import {
  changePassword as changePasswordRequest,
  deleteCurrentUser as deleteCurrentUserRequest,
  logOut,
} from "@/services/authService";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useCallback } from "react";
import {
  normalizeUserProfile,
  type UserProfile,
} from "@/preferences/types";
import { usePreferencesStore } from "@/stores/usePreferencesStore";
import { logError } from "@/utils/logger";
import {
  accessTokenResponseSchema,
  parseApiResponse,
  userProfileResponseSchema,
} from "@/api/responseSchemas";
import { captureEntityMutation, identifyAnalyticsUser, resetAnalyticsUser } from "@/analytics/posthog";

export type Credentials = { email: string; password: string };
export type MeResponse = {
  access_token: string;
};

export type UpdateUserProfilePayload = {
  email?: string;
  firstname?: string | null;
  lastname?: string | null;
  phone?: string | null;
};
export type ChangePasswordPayload = { oldPassword: string; newPassword: string };

const applyProfileToStores = (profile: UserProfile | null) => {
  useAuthStore.getState().setUser(profile);
  useAuthStore.getState().setProfileHydrated(true);
  usePreferencesStore.getState().hydrateFromServer(profile);
};

let sessionInitialization: Promise<void> | null = null;
let profileRequest: { token: string; promise: Promise<UserProfile | null> } | null = null;
let deleteCurrentUserInFlight: Promise<boolean> | null = null;

const clearLegacyTokens = () => {
  try {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  } catch {
    // Authentication no longer depends on browser storage.
  }
};

const clearUserScopedStorage = (userId: string | undefined) => {
  if (!userId || typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(`hey-jerry-dashboard-layout:v1:${userId}`);
  } catch {
    // Authentication cleanup must not prevent the redirect.
  }
};

const initializeSession = () => {
  if (sessionInitialization) return sessionInitialization;

  sessionInitialization = (async () => {
    clearLegacyTokens();
    try {
      const response = await apiClient.post("/auth/refresh");
      const data = parseApiResponse(accessTokenResponseSchema, response.data, "POST /auth/refresh");
      useAuthStore.getState().setToken(data.access_token);
      useAuthStore.getState().setProfileHydrated(false);
    } catch {
      useAuthStore.getState().signOut();
    } finally {
      useAuthStore.getState().setHasHydrated(true);
    }
  })();

  return sessionInitialization;
};

const readProfileFromResponse = (data: unknown): UserProfile | null => {
  if (!data || typeof data !== "object") {
    return null;
  }

  return normalizeUserProfile(
    parseApiResponse(userProfileResponseSchema, data, "PATCH /users"),
  );
};

export function useSignIn() {
  const setToken = useAuthStore((s: AuthState) => s.setToken);
  const setUser = useAuthStore((s: AuthState) => s.setUser);
  const setProfileHydrated = useAuthStore((s: AuthState) => s.setProfileHydrated);

  const mutationFn = async (credentials: Credentials): Promise<MeResponse> => {
    const res = await apiClient.post("/auth/login", credentials);
    return parseApiResponse(accessTokenResponseSchema, res.data, "POST /auth/login");
  };

  return useMutation<MeResponse, unknown, Credentials, unknown>({
    mutationFn,
    onSuccess: (data: MeResponse) => {
      resetAnalyticsUser();
      queryClient.clear();
      setToken(data.access_token);
      setUser(null);
      setProfileHydrated(false);
    },
    onError: () => {
      // Silent error handling
    },
  });
}

export function useAuth() {
  // expose basic auth helpers for components that expect a `useAuth` hook
  const user = useAuthStore((s: AuthState) => s.user);
  const token = useAuthStore((s: AuthState) => s.token);
  const profileHydrated = useAuthStore((s: AuthState) => s.profileHydrated);
  const hasHydrated = useAuthStore((s: AuthState) => s.hasHydrated);
  const signIn = useSignIn();
  const signOutStore = useAuthStore((s: AuthState) => s.signOut);
  const navigate = useNavigate();

  useEffect(() => {
    void initializeSession();
  }, []);

  const fetchUserProfile = useCallback(async () => {
    const requestToken = useAuthStore.getState().token;
    if (!requestToken) return null;
    if (profileRequest?.token === requestToken) return profileRequest.promise;

    const operation = (async () => {
      try {
        const res = await apiClient.get("/users/");
        const responseProfile = parseApiResponse(
          userProfileResponseSchema.nullable(),
          res.data,
          "GET /users",
        );
        const profile = normalizeUserProfile(responseProfile);

        // A response from a previous session must not hydrate the current one.
        if (useAuthStore.getState().token !== requestToken) return null;
        applyProfileToStores(profile);
        return profile;
      } catch (error) {
        logError("useAuth.fetchUserProfile", error);
        if (useAuthStore.getState().token === requestToken && axios.isAxiosError(error) && error.response?.status === 401) {
          resetAnalyticsUser();
        }
        return null;
      }
    })();
    profileRequest = { token: requestToken, promise: operation };

    try {
      return await operation;
    } finally {
      if (profileRequest?.promise === operation) profileRequest = null;
    }
  }, []);

  const updateUserProfile = useCallback(async (payload: UpdateUserProfilePayload) => {
    try {
      const res = await apiClient.patch("/users/", payload);
      const responseProfile = readProfileFromResponse(res.data);
      captureEntityMutation({
        entity: "profile",
        operation: "update",
        result: "success",
        changedFields: Object.keys(payload),
      });

      if (responseProfile) {
        applyProfileToStores(responseProfile);
        return responseProfile;
      }

      const fallbackProfile = await fetchUserProfile();

      if (fallbackProfile) {
        return fallbackProfile;
      }

      const currentUser = useAuthStore.getState().user;
      const mergedProfile: UserProfile | null = currentUser
        ? {
            ...currentUser,
            email: payload.email ?? currentUser.email,
            firstname: payload.firstname ?? currentUser.firstname,
            lastname: payload.lastname ?? currentUser.lastname,
            phone: payload.phone ?? currentUser.phone,
          }
        : null;

      if (mergedProfile) {
        applyProfileToStores(mergedProfile);
      }

      return mergedProfile;
    } catch (error) {
      captureEntityMutation({
        entity: "profile",
        operation: "update",
        result: "failure",
        error,
        changedFields: Object.keys(payload),
      });
      logError("useAuth.updateUserProfile", error);
      return null;
    }
  }, [fetchUserProfile]);

  const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
    try {
      await changePasswordRequest(payload);
      captureEntityMutation({
        entity: "profile",
        operation: "change_password",
        result: "success",
        changedFields: ["password"],
      });
      return true;
    } catch (error) {
      captureEntityMutation({
        entity: "profile",
        operation: "change_password",
        result: "failure",
        error,
        changedFields: ["password"],
      });
      logError("useAuth.changePassword", error);
      return false;
    }
  }, []);

  const deleteCurrentUser = useCallback(async () => {
    if (deleteCurrentUserInFlight) return deleteCurrentUserInFlight;

    const operation = (async () => {
      try {
        await deleteCurrentUserRequest();
      } catch (error) {
        captureEntityMutation({
          entity: "profile",
          operation: "delete",
          result: "failure",
          error,
        });
        logError("useAuth.deleteCurrentUser", error);
        return false;
      }

      captureEntityMutation({
        entity: "profile",
        operation: "delete",
        result: "success",
      });
      await queryClient.cancelQueries();
      queryClient.clear();
      clearLegacyTokens();
      clearUserScopedStorage(useAuthStore.getState().user?.id);
      signOutStore();
      usePreferencesStore.getState().resetToDefaults();
      resetAnalyticsUser();
      await navigate({ to: "/auth/signin", replace: true });
      return true;
    })();

    deleteCurrentUserInFlight = operation;
    try {
      return await operation;
    } finally {
      if (deleteCurrentUserInFlight === operation) deleteCurrentUserInFlight = null;
    }
  }, [navigate, signOutStore]);

  const logout = async () => {
    try {
      await logOut();
    } catch (error) {
      logError("useAuth.logout", error);
    }

    signOutStore();
    queryClient.clear();
    resetAnalyticsUser();
    usePreferencesStore.getState().resetToDefaults();
    navigate({ to: "/auth/signin", replace: true });
  };

  // Auto-fetch profile after auth rehydrate or token refresh.
  useEffect(() => {
    if (hasHydrated && token && !profileHydrated) {
      void fetchUserProfile();
    }
  }, [hasHydrated, token, profileHydrated, fetchUserProfile]);

  useEffect(() => {
    if (!hasHydrated) return;

    if (token && profileHydrated && user?.id) {
      identifyAnalyticsUser(String(user.id));
      return;
    }

    if (!token || profileHydrated) resetAnalyticsUser();
  }, [hasHydrated, profileHydrated, token, user?.id]);

  return {
    user,
    token,
    profileHydrated,
    signIn,
    logout,
    fetchUserProfile,
    updateUserProfile,
    changePassword,
    deleteCurrentUser,
  };
}
